/**
 * HUD sampler — sits between the WS SnapshotSource and React state.
 *
 * Snapshots flow into WP6's ring buffer at 30 Hz (see WsSnapshotSource +
 * WP6 mountRenderer). React state does NOT need that rate: the HUD is
 * a health bar, a cast bar, a countdown timer. We sample the latest
 * snapshot into `state.hudSnapshot` at 10 Hz and also dispatch a
 * `hud.tick` so time-dependent HUD elements (countdown, rejection
 * toasts) refresh at the same cadence.
 *
 * The sampler is decoupled from the socket layer — it just wraps
 * WsSnapshotSource.subscribe(). Multiple mounts would clobber the
 * source's single-subscriber slot, so we register-unregister carefully.
 * The renderer also subscribes (via mountRenderer). To keep BOTH
 * consumers happy, the sampler taps snapshots by keeping the LATEST
 * pushed frame in a ref, using a `latestSampler` companion on the
 * source. See `WsSnapshotSource` — it fans to one consumer; we sample
 * from within its push by wiring an interceptor.
 *
 * Simpler alternative: give the sampler its OWN latest-frame reference
 * updated by an accompanying handler on the socket layer. That's the
 * approach we take — the sampler subscribes to the raw snapshot source
 * OUTSIDE the WP6 stream by exposing an "onEach" listener.
 *
 * For MVP, we take a compromise that avoids fan-out complexity:
 * the sampler polls `source.latestSnapshot()` on a timer. The source
 * exposes the last frame it saw; both consumers get correct data.
 */

import { useEffect } from "react";
import type { WsSnapshotSource } from "../net/snapshotSource.js";
import { useStore } from "./context.js";

/** HUD sample interval in ms. 10 Hz = 100ms. */
export const HUD_SAMPLE_INTERVAL_MS = 100;

/**
 * Subscribe React to the snapshot source at HUD rate. Uses `setInterval`
 * (not rAF) — the HUD doesn't need frame-perfect timing, and the
 * reducer needs a stable cadence for rejection-toast pruning.
 */
export function useHudSampler(source: WsSnapshotSource): void {
  const { dispatch } = useStore();
  useEffect(() => {
    const id = window.setInterval(() => {
      const snap = source.latestSnapshot();
      if (snap !== null) {
        dispatch({ type: "hud.sample", snapshot: snap });
      }
      dispatch({ type: "hud.tick" });
    }, HUD_SAMPLE_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [source, dispatch]);
}
